import { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router";
import { useI18n } from "i18n";
import logger from "logging/logger";
import useNotificationActions from "modules/notification/hooks/useNotificationActions";
import useCompanySettings from "modules/company/hooks/useCompanySettings";
import { signInWithSAML } from "modules/auth/utils/socialAuth";
import useAuth from "modules/auth/hooks/useAuth";
import { IEvent } from "modules/event/types";
import useQueryParams from "modules/queryParams/hooks/useQueryParams";
import { hasAuthenticated } from "modules/queryParams/queryParamsHelper";
import { selectCurrentEvent } from "modules/event/selectors";
import { useSelector } from "react-redux";

interface UseExistingAuthCheckerArgs {
  setRedirectingToSSO: (redirecting: boolean) => void;
  needCheck: boolean;
  clearIntervalRef?: () => void;
}

export const useEventSso = ({
  setRedirectingToSSO,
  needCheck,
  clearIntervalRef = () => {},
}: UseExistingAuthCheckerArgs): boolean => {
  const history = useHistory();
  const [isSAMLEnabled, setIsSAMLEnabled] = useState(false);
  const event = useSelector(selectCurrentEvent);
  const { getCompanyPublicSettings } = useCompanySettings();
  const { addErrorNotification } = useNotificationActions();
  const { t } = useI18n(["server"]);
  const { isAuthenticated } = useAuth();
  const params = useQueryParams();
  const isAuthState = hasAuthenticated(params);

  const { pathname } = history.location;

  const isSSO = pathname.substring(pathname.lastIndexOf("/") + 1) === "sso";

  const getSAMLProvider = useCallback(
    async (evt: IEvent) => {
      const companyId =
        typeof evt.company === "string" ? evt.company : evt.company?.id;
      const eventCompanySettings = await getCompanyPublicSettings(companyId);
      if (!eventCompanySettings) return null;

      const isCompanyIdentityProvider =
        eventCompanySettings &&
        eventCompanySettings.identityProviders?.type === "saml2";

      if (!isCompanyIdentityProvider) return null;

      return eventCompanySettings.identityProviders;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    const startSsoProcess = async () => {
      if (!event) return;

      setRedirectingToSSO(false);
      try {
        const SAMLProvider = await getSAMLProvider(event);
        setIsSAMLEnabled(Boolean(SAMLProvider?.isEnabled));

        if (!SAMLProvider) {
          logger.warn(
            "[useEventSso] no SAML provider found, redirecting back to the event landing page",
            {
              SAMLProvider,
            },
          );
          history.push(`/e/${event.code}/register`);
          return;
        }

        const startSSOSignInProcess =
          !isAuthenticated && SAMLProvider?.isEnabled && (isAuthState || isSSO);
        if (startSSOSignInProcess) {
          setRedirectingToSSO(true);
          await signInWithSAML(SAMLProvider.providerId, true, true);
          return;
        }
      } catch (error) {
        type errorType = { message: string; data: { name: string } };
        const typedError = error as errorType;

        addErrorNotification({
          message: t(typedError.message, { name: typedError?.data?.name }),
          autoDismiss: 8,
        });
        logger.error(
          "[useEventSso] (startSsoProcess) errors during sso process",
          typedError,
        );
      }
    };

    if (needCheck) {
      clearIntervalRef();
      startSsoProcess();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event, needCheck, isAuthenticated]);

  return isSAMLEnabled;
};
